// Request deduplication and cancellation utility
class RequestManager {
    private pendingRequests: Map<string, AbortController> = new Map();
    private requestCache: Map<string, { data: any; timestamp: number }> = new Map();
    private readonly CACHE_DURATION = 30000; // 30 seconds

    // Generate a unique key for the request
    private getRequestKey(url: string, options?: RequestInit): string {
        const method = options?.method || 'GET';
        const body = options?.body ? JSON.stringify(options.body) : '';
        return `${method}:${url}:${body}`;
    }

    // Check if request is already pending
    isRequestPending(url: string, options?: RequestInit): boolean {
        const key = this.getRequestKey(url, options);
        return this.pendingRequests.has(key);
    }

    // Check if we have cached data that's still valid
    getCachedData(url: string, options?: RequestInit): any | null {
        const key = this.getRequestKey(url, options);
        const cached = this.requestCache.get(key);

        if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
            return cached.data;
        }

        return null;
    }

    // Make a request with deduplication and caching
    async makeRequest(url: string, options?: RequestInit): Promise<any> {
        const key = this.getRequestKey(url, options);

        // Check cache first
        const cachedData = this.getCachedData(url, options);
        if (cachedData) {
            return cachedData;
        }

        // If request is already pending, wait for it instead of making a new one
        if (this.pendingRequests.has(key)) {
            return this.waitForPendingRequest(key);
        }

        // Create new request
        const controller = new AbortController();
        this.pendingRequests.set(key, controller);

        try {
            const response = await fetch(url, {
                ...options,
                signal: controller.signal
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            // Cache the response
            this.requestCache.set(key, {
                data,
                timestamp: Date.now()
            });

            return data;
        } catch (error) {
            if (error instanceof Error && error.name === 'AbortError') {
                throw new Error('Request was cancelled');
            }
            throw error;
        } finally {
            this.pendingRequests.delete(key);
        }
    }

    // Wait for a pending request to complete
    private async waitForPendingRequest(key: string): Promise<any> {
        return new Promise((resolve, reject) => {
            const checkPending = () => {
                if (!this.pendingRequests.has(key)) {
                    // Request completed, check cache
                    const cached = this.requestCache.get(key);
                    if (cached) {
                        resolve(cached.data);
                    } else {
                        reject(new Error('Pending request failed'));
                    }
                } else {
                    // Still pending, check again in 100ms
                    setTimeout(checkPending, 100);
                }
            };
            checkPending();
        });
    }

    // Cancel a specific request
    cancelRequest(url: string, options?: RequestInit): void {
        const key = this.getRequestKey(url, options);
        const controller = this.pendingRequests.get(key);

        if (controller) {
            controller.abort();
            this.pendingRequests.delete(key);
        }
    }

    // Cancel requests that don't match the current context
    cancelRequestsExcept(keepUrl: string, keepOptions?: RequestInit): void {
        const keepKey = this.getRequestKey(keepUrl, keepOptions);

        this.pendingRequests.forEach((controller, key) => {
            if (key !== keepKey) {
                controller.abort();
                this.pendingRequests.delete(key);
            }
        });
    }

    // Cancel all pending requests
    cancelAllRequests(): void {
        this.pendingRequests.forEach((controller, key) => {
            controller.abort();
        });
        this.pendingRequests.clear();
    }

    // Clear cache
    clearCache(): void {
        this.requestCache.clear();
    }

    // Get pending requests count
    getPendingRequestsCount(): number {
        return this.pendingRequests.size;
    }
}

// Export singleton instance
export const requestManager = new RequestManager();
